import { type PropsWithChildren, useMemo, useState } from 'react';
import TreeComponent from 'components/TreeComponent';
import { useDebounce } from 'hooks/useDebounce';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import {
  type ObjectTypeWithFilter,
  handleCombineObjectTypesWithFilter,
} from 'utils/objectTypes';
import type { TType } from '@timeedit/types';
import { Popover } from '@timeedit/ui-components/antd';

type PropsObjectTypeTreePopover = PropsWithChildren<{
  onClick: (val: number | string) => void;
  onlyLeafNode?: boolean;
}>;

type TObjectTypeNode = {
  key: number;
  title: string;
  disabled?: boolean;
  children?: TObjectTypeNode[];
};

export const ObjectTypeTreePopover = ({
  onClick,
  onlyLeafNode = true,
  children,
}: PropsObjectTypeTreePopover) => {
  const [searchValue, setSearchValue] = useState('');
  const objectTypesList: TType[] = useAppSelector(selectObjectTypesList);
  const debounceSearchValue = useDebounce(searchValue);

  const treeData: TObjectTypeNode[] = useMemo(() => {
    const { dataFilter } = handleCombineObjectTypesWithFilter(
      onlyLeafNode
        ? objectTypesList.filter((item) => item.extId !== 'root')
        : objectTypesList,
      (item) => {
        if (!item?.name || !debounceSearchValue) return true;
        return item.name
          .toLowerCase()
          .includes(debounceSearchValue.toLowerCase());
      }
    );

    return convertDataPopup(dataFilter, onlyLeafNode);
  }, [objectTypesList, onlyLeafNode, debounceSearchValue]);

  return (
    <Popover
      zIndex={10000}
      placement="topRight"
      trigger={['click']}
      content={
        <TreeComponent
          onSelect={(selectedKeys: TObjectTypeNode['key'][]) => {
            onClick(selectedKeys[0]);
          }}
          treeData={treeData}
          handleSearch={setSearchValue}
        />
      }
    >
      {children}
    </Popover>
  );
};

export const convertDataPopup = (
  tree: ObjectTypeWithFilter[],
  onlyLeafNodes?: boolean
): TObjectTypeNode[] => {
  return tree.map((item) => {
    const children = item.children
      ? convertDataPopup(item.children, onlyLeafNodes)
      : undefined;

    return {
      title: item.title,
      key: item.key,
      disabled: !(
        item.allowAbstractObjects ||
        item.allowStandardObjects ||
        item.allowVirtualObjects
      ),
      children,
    };
  });
};
